import React, { useEffect, useMemo, useState } from 'react'
import { withAccounts } from '../../../hoc/withAccounts'
import { AccountsOverviewResult, UspCollectionWorklistGetNextAccountResult, UspDealFlagViewResult } from '../../../models'
import Header from '../Header/Header'
import { Box, Card, CardHeader, Container, generateUtilityClasses, Grid, Paper } from '@mui/material'
import { styled } from '@mui/system'
import BankruptcyCard from './components/BankruptcyCard/BankruptcyCard'
import CommentsCard from './components/CommentsCard/CommentsCard'
import { Form } from 'react-final-form'
import Button from '@mui/material/Button'
import { appRoute } from '../../../constants'
import { useNavigate, useParams } from 'react-router-dom'
import { transformToFormValues, transformToRequestValues } from './transform'
import {
  useAddDealsFlagMutation,
  useEditAccountBankruptcyMutation,
  useLazyGetAccountBankruptcyQuery,
  useWorklistNextAccountMutation,
} from '../../../services'
import { useDispatch, useSelector } from 'react-redux'
import { enqueueNotification } from '../../../redux/slices/notificationsSlice'
import { CustomCircular } from '../../../ui'
import { getWorklistAccountID, getWorklistID } from '../../../redux/slices/collectionsSlice'
import ReactRouterPrompt from 'react-router-prompt'
import ConfirmDialog from '../../../components/ConfirmDialog'
import { getUserAccessRights } from '../../../redux/slices/authSlice'

const classes = generateUtilityClasses('form', ['root', 'pageBody', 'formItem', 'buttonWrap', 'saveBtn'])

const StyledGrid = styled('form')(({ theme }) => ({
  [`&.${classes.root}`]: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  [`& .${classes.pageBody}`]: {
    padding: theme.spacing(2, 2, 2, 2),
    display: 'flex',
    alignItems: 'flexStart',
    justifyContent: 'justify-start',
  },
  [`& .${classes.formItem}`]: {
    padding: theme.spacing(2, 3, 1, 3),
    margin: theme.spacing(0),
    borderRadius: 0,
  },
  [`& .${classes.buttonWrap}`]: {
    display: 'flex',
    justifyContent: 'flex-start',
    flexDirection: 'row-reverse',
    padding: theme.spacing(3, 3, 3, 3),
    marginTop: 'auto',
  },
  [`& .${classes.saveBtn}`]: {
    marginLeft: theme.spacing(2),
  },
}))

interface Props {
  accountsData: AccountsOverviewResult
  setDirty: any
}

const AccountsBankruptcy = ({ accountsData, setDirty }: Props) => {
  const { dealID } = useParams()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const userAccess = useSelector(getUserAccessRights)
  const [formChanged, setFormChanged] = useState(false)
  const worklistAccountID = useSelector(getWorklistAccountID)
  const worklistID = useSelector(getWorklistID)
  const disableEditIcons = userAccess?.CanEditAccount
  const [initialValues, setInitialValues] = useState({})
  const [getBankruptcyData, { data, isLoading: getBankruptcyDataIsLoading }] = useLazyGetAccountBankruptcyQuery()
  const [editBankruptcyData, { isSuccess: editBankruptcySuccess }] = useEditAccountBankruptcyMutation()
  const [nextAccount, { data: nextAccountData, isSuccess: nextAccountIsSuccess }] = useWorklistNextAccountMutation()
  const [isAddEditFlagModalOpen, setIsAddEditFlagModalOpen] = useState(false)
  const [addAccountFlags, { isSuccess: addAccountFlagIsSuccess, isLoading: addAccountFlagsIsLoading }] = useAddDealsFlagMutation()
  const [selectedFlag, setSelectedFlag] = useState<UspDealFlagViewResult | undefined>()

  const disableFields = !userAccess?.CanEditAccountTracking

  useEffect(() => {
    getBankruptcyData({ dealId: dealID as string })
  }, [])

  useEffect(() => {
    setInitialValues(transformToFormValues(data))
  }, [data])

  const handleSubmit = (values: any) => {
    if (dealID) {
      editBankruptcyData({
        dealId: dealID as string,
        editBankruptcy: transformToRequestValues(values),
      })
    }
  }

  const handleNextAccount = () => {
    if (worklistID && worklistAccountID) {
      nextAccount({
        worklistId: worklistID.toString(),
        uspCollectionWorklistGetNextAccount: {
          worklistAccountID: worklistAccountID,
          isNext: true,
        },
      })
    }
  }
  const handlePreviousAccount = () => {
    if (worklistID && worklistAccountID) {
      nextAccount({
        worklistId: worklistID.toString(),
        uspCollectionWorklistGetNextAccount: {
          worklistAccountID: worklistAccountID,
          isNext: false,
        },
      })
    }
  }

  useEffect(() => {
    if (editBankruptcySuccess) {
      setFormChanged(false)
      dispatch(enqueueNotification({ message: 'Success.' }))
      navigate(appRoute?.AccountsBankruptcy.replace(':dealID', dealID as string))
      getBankruptcyData({ dealId: dealID as string })
    }
  }, [editBankruptcySuccess])

  useEffect(() => {
    if (addAccountFlagIsSuccess) {
      dispatch(enqueueNotification({ message: 'Success' }))
      setFormChanged(false)
      setIsAddEditFlagModalOpen(false)
    }
  }, [addAccountFlagIsSuccess])

  const isLoading = useMemo(() => getBankruptcyDataIsLoading, [getBankruptcyDataIsLoading])

  return (
    <Container>
      <Grid container>
        <Grid item xs={12} md={10} spacing={2}>
          <Form onSubmit={handleSubmit} initialValues={initialValues}>
            {({ handleSubmit, valid, dirty }: any) => {
              if (setDirty) setDirty(dirty)
              return (
                <form onSubmit={handleSubmit} onChange={() => setFormChanged(true)}>
                  <ReactRouterPrompt when={formChanged} beforeConfirm={async () => {}}>
                    {({ isActive, onConfirm, onCancel }) => (
                      <ConfirmDialog
                        open={!!isActive}
                        onOk={onConfirm}
                        handleClose={onCancel}
                        notification={'Warning: Your changes haven’t been saved.'}
                      />
                    )}
                  </ReactRouterPrompt>
                  <Card sx={{ overflow: 'visible', display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Grid item xs={12}>
                      <CardHeader title='Bankruptcy' />

                      <BankruptcyCard disableFields={disableFields} onDropdownFormChange={() => setFormChanged(true)} />
                    </Grid>

                    <CommentsCard disableFields={disableFields} />
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 2,
                        position: 'sticky',
                        bottom: 16,
                        right: 20,
                        p: 3,
                        width: 'fit-content',
                        alignSelf: 'flex-end',
                      }}
                    >
                      <Button disabled={!(valid && dirty)} type='submit' className={classes.saveBtn} variant='contained'>
                        Save
                      </Button>
                    </Box>
                  </Card>
                </form>
              )
            }}
          </Form>
          <CustomCircular open={isLoading} />
        </Grid>
      </Grid>
    </Container>
  )
}

export default withAccounts(AccountsBankruptcy)
